import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {ContextualMenuInputsOption} from '@Components/contextual-menu/components/contextual-menu-inputs-option';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetKeyFromFancyTextItem} from '@Hooks/poster-editor/useGetKeyFromFancyTextItem';

export function EditFancyTextOption(): ReactElement {
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const text = useGetKeyFromFancyTextItem('text');
  const [isChanging, setIsChanging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  if (!activeItem || text === undefined) {
    throw new Error('Active item does not exist in edit fancy text option');
  }

  if (!activeItem.isFancyText()) {
    throw new Error(`Expected fancy text item, instead got ${activeItem.gitype}`);
  }

  useEffect(() => {
    if (!isChanging) {
      bringInputToFocus();
    }
  }, [isChanging]);

  const updateText = (text: string): void => {
    setIsChanging(true);
    void activeItem
      .updateFromObject({
        text,
      })
      .finally((): void => {
        setIsChanging(false);
      });
  };

  const bringInputToFocus = (): void => {
    if (inputRef && inputRef.current) {
      inputRef.current.focus();
    }
  };

  const onChangeTextEnded = (value: string): void => {
    if (value === '') {
      return;
    }
    updateText(value);
  };

  return (
    <ContextualMenuInputsOption
      refObject={inputRef}
      key={activeItem.uid}
      id="edit-fancy-text-option"
      onInputChangeEnded={onChangeTextEnded}
      value={text}
      isControlled={false}
      isDisabled={isChanging}
      elementTitle={window.i18next.t('pmwjs_add_your_text')}
    />
  );
}
